"use client"

import { cn } from "@/lib/utils"
import { motion } from "@/components/insurance/static-motion"
import { useInsuranceTranslation } from "@/lib/insurance/use-insurance-translation"
import { getInsurerLogoUrl } from "@/lib/insurance/insurer-assets"
import type { Insurer } from "@/lib/insurance/insurance-types"

interface InsurerDropdownProps {
  insurers: Insurer[]
  insurerId: string
  onInsurerChange: (id: string) => void
  onFreetext: (text: string) => void
  insurerFreetext: string
  onAdvance: () => void
}

export function InsurerDropdown({
  insurers,
  insurerId,
  onInsurerChange,
  onFreetext,
  insurerFreetext,
  onAdvance,
}: InsurerDropdownProps) {
  const { t } = useInsuranceTranslation()

  const isOther = insurerId === "other"

  return (
    <div>
      {/* Insurer grid — always visible */}
      <div
        className="insurer-grid-dropdown"
        role="listbox"
        aria-label={t("gate.section.insurer")}
      >
        {insurers.map((ins) => (
          <InsurerOption
            key={ins.id}
            insurer={ins}
            selected={ins.id === insurerId}
            onSelect={onInsurerChange}
          />
        ))}
        {/* "Other insurer" option */}
        <div
          className={cn("insurer-option", insurerId === "other" && "selected")}
          role="option"
          aria-selected={insurerId === "other"}
          tabIndex={0}
          onClick={() => onInsurerChange("other")}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              onInsurerChange("other")
            }
          }}
        >
          <span className="option-logo logo-other">?</span>
          <span className="option-name">{t("form.insurer.other")}</span>
          {insurerId === "other" && (
            <svg
              className="option-check"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="14"
              height="14"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
      </div>

      {/* Other insurer freetext */}
      {isOther && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
          style={{ marginTop: 12 }}
        >
          <input
            type="text"
            value={insurerFreetext}
            onChange={(e) => onFreetext(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && insurerFreetext.trim()) {
                e.preventDefault()
                onAdvance()
              }
            }}
            placeholder={t("form.insurer.freetext_placeholder")}
            aria-label={t("form.insurer.freetext_label")}
            className="gate-input"
            autoFocus
          />
          <button
            type="button"
            className="btn-primary"
            onClick={onAdvance}
            disabled={!insurerFreetext.trim()}
            style={{ marginTop: 12 }}
          >
            {t("gate.continue")}
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>
        </motion.div>
      )}
    </div>
  )
}

function InsurerOption({
  insurer,
  selected,
  onSelect,
}: {
  insurer: Insurer
  selected: boolean
  onSelect: (id: string) => void
}) {
  const logo = getInsurerLogoUrl(insurer)
  return (
    <div
      className={cn("insurer-option", selected && "selected")}
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={() => onSelect(insurer.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          onSelect(insurer.id)
        }
      }}
    >
      <span className="option-logo">
        {logo ? (
          // Inline max-size so the logo never balloons to its natural size
          // before coverage-form.css (a separate client chunk) applies.
          <img
            src={logo}
            alt=""
            style={{ maxWidth: 32, maxHeight: 32, objectFit: "contain" }}
          />
        ) : (
          <span>{insurer.name.charAt(0)}</span>
        )}
      </span>
      <span className="option-name">{insurer.name}</span>
      {selected && (
        <svg
          className="option-check"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          width="14"
          height="14"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  )
}
